import React from 'react'

import styled from 'theme/styled-components'
import useTheme from 'theme/useTheme'

import RoomImage from 'components/image/RoomImage'
import Icons from 'components/icons/Icons'

import { getRoomTypeEnum } from 'map/utils'
import { getRoomTypeColor } from './POIUtils'

interface Props {
  poi: TotemAreaV6
  onClick: (uniquePlaceName: string) => void
  onItinerary?: (uniquePlaceName: string) => void
}

const TotemPOIDetails = ({ poi, onClick, onItinerary }: Props) => {
  const [theme] = useTheme()

  return (
    <POIResult key={poi.id} onClick={() => onClick(poi.uniquePlaceName)} hasIniteraryButton={!!onItinerary}>
      <LeftContainer>
        <ImageContainer>
          <RoomImage roomType={poi.placeType} iconSize={120} height={200} width={200} radius={10} />
        </ImageContainer>
        <MiddleContainer>
          <POITypeContainer backgroundColor={getRoomTypeColor(theme, getRoomTypeEnum(poi.placeType))}>
            <POIType>{poi.placeType}</POIType>
          </POITypeContainer>
          <POIName>{poi.name}</POIName>
          <POILocation>
            {poi.building} • {poi.floor}
          </POILocation>
        </MiddleContainer>
      </LeftContainer>
      {onItinerary && (
        <RightContainer>
          <ItineraryButton
            onClick={(event) => {
              event.stopPropagation()
              onItinerary(poi.uniquePlaceName)
            }}>
            <Icons name="itinerary" size={80} color={theme.colors.blue} />
          </ItineraryButton>
        </RightContainer>
      )}
    </POIResult>
  )
}

export default TotemPOIDetails

const POIResult = styled('div')<{ hasIniteraryButton: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: space-between;
  gap: 20px;
  width: 100%;
  height: 200px;
  transition: all 0.1s ease-in-out;
`

const LeftContainer = styled('div')`
  display: flex;
  flex-grow: 1;
  justify-content: left;
  align-items: center;
  gap: 40px;
`

const ImageContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 200px;
  height: 200px;
  background-color: ${(props) => props.theme.colors.white};
  border-radius: 10px;
`

const MiddleContainer = styled('div')`
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: space-evenly;
`

const POITypeContainer = styled('div')<{ backgroundColor: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: fit-content;
  padding: 8px 12px;
  background-color: ${(props) => props.backgroundColor};
  border-radius: 5px;
`

const POIType = styled('p')`
  ${(props) => props.theme.fonts.totemBigLight};
  font-size: 28px;
  margin: 0;
  color: ${(props) => props.theme.colors.darkGrey};
`

const POIName = styled('p')`
  ${(props) => props.theme.fonts.totemBigLight};
  margin: 0;
`

const POILocation = styled('p')`
  ${(props) => props.theme.fonts.totemBigLight};
  font-size: 32px;
  margin: 0;
  color: ${(props) => props.theme.colors.darkGrey};
`

const RightContainer = styled('div')`
  display: flex;
  justify-content: right;
  align-items: center;
  width: 200px;
  margin-left: 40px;
`
const ItineraryButton = styled('button')`
  ${(props) => props.theme.fonts.totemBigLight};
  border: 1px solid ${(props) => props.theme.colors.middleGrey};
  border-radius: 5px;
  transition: all 0.2s ease-in-out;
  padding: 20px;

  &:active {
    transform: scale(0.95);
    background-color: ${(props) => props.theme.colors.paleBlue};
  }
`
